import m from 'mithril'
import {debounce} from 'lodash'

import s from './styles.module.scss'

import IconBracket from '@app/icons/bracket-right'
import IconSignalGrayscale from '@app/icons/signal-grayscale'
import IconPhase from '@app/icons/phase'
import IconRoute from '@app/icons/route'
import IconRoad from '@app/icons/road'
import IconNetworkGeneral from '@app/icons/network-general'
import IconDistrict from '@app/icons/district'

import Datalist from '@app/components/input-datalist'

import ActionButton from '@app/components/input-action-button'
import Popup from '@app/components/popup'
import DotPulseLoader from '@app/components/loader-dot-pulse'


import router, {MODES} from '@app/router'
import { ENTITY_TYPES } from '@shared/constants'

import SearchModel from '@app/models/search'
import { EDGE_GROUPING_CRITERIA } from '@shared-schemas/entities'


const InputSearch = {}

InputSearch.oninit = (vnode) => {

	vnode.state.search = debounce(async () => {

		const catalogResults = (raw_results) => {

			const {
				current_mode,
			} = vnode.state

			const unsorted_results = [

				...(
					(raw_results?.entities?.customers?.length > 1) ? raw_results?.entities?.customers?.map(c => ({
						id: c.id,
						title: 'Account',
						name: c.name,
						link: router.routes.system.useNavigation({
							params: {
								customer_id: c.id,
							},
						}),
						similarity: c.similarity,
					}))
					: []
				),

				...(
					raw_results?.entities?.regions?.map(r => ({
						id: r.id,
						icon: IconDistrict,
						title: 'Region',
						name: r.name,
						link: router.routes[`${current_mode}_region`].useNavigation({
							params: {
								region_id: r.id,
							},
							query: {
								active_sidebar: (current_mode === MODES.SIGOPS) ? 'none' : 'left',
							},
							options: {
								preserve: true
							},
						}),
						similarity: r.similarity,
					})) ?? []
				),

				...(
					raw_results?.entities?.subregions?.map(subr => ({
						id: subr.id,
						icon: IconDistrict,
						title: 'Subregion',
						name: subr.name,
						link: router.routes[`${current_mode}_subregion`].useNavigation({
							params: {
								region_id: subr.region_id,
								subregion_id: subr.id,
							},
							options: {
								preserve: true
							},
						}),
						similarity: subr.similarity,
					})) ?? []
				),

				...(
					raw_results?.entities?.networks?.map(n => ({
						id: n.id,
						icon: IconNetworkGeneral,
						title: 'Network',
						name: n.name,
						link: router.routes[`${current_mode}_network`].useNavigation({
							params: {
								region_id: n.region_id,
								subregion_id: n.subregion_id,
								network_id: n.id,
							},
							query: {
								active_sidebar: (current_mode === MODES.SIGOPS) ? 'right' : 'left',
							},
							options: {
								preserve: true
							},
						}),
						similarity: n.similarity,
					})) ?? []
				),

				...(
					raw_results?.entities?.signals?.map(s => ({
						id: s.id,
						icon: IconSignalGrayscale,
						title: 'Signal',
						name: (s.external_signal_id && s.external_signal_id !== s.name && s.external_signal_id !== s.name_short) ? `#${s.external_signal_id} (${s.name_short || s.name})` : `${s.name_short || s.name}`,
						link: router.routes[`${current_mode}_signal`].useNavigation({
							params: {
								region_id: s.region_id,
								subregion_id: s.subregion_id,
								network_id: s.network_id,
								signal_id: s.id,
							},
							query: {
								active_sidebar: 'right',
							},
							options: {
								preserve: true
							},
						}),
						similarity: s.similarity,
					})) ?? []
				),

				...(
					raw_results?.entities?.phases?.map(p => ({
						id: p.id,
						icon: IconPhase,
						title: 'Phase',
						name: (p.external_signal_id && p.external_signal_id !== p.signal_name && p.external_signal_id !== p.signal_name_short) ? `#${p.external_signal_id} (${p.signal_name_short || p.signal_name}) > ${p.name}` : `${p.signal_name_short || p.signal_name} > ${p.name}`,
						link: router.routes[`${current_mode}_phase`].useNavigation({
							params: {
								region_id: p.region_id,
								subregion_id: p.subregion_id,
								network_id: p.network_id,
								signal_id: p.signal_id,
								phase_id: p.id,
							},
							query: {
								active_sidebar: 'right',
							},
							options: {
								preserve: true
							},
						}),
						similarity: p.similarity,
					})) ?? []
				),

				...(
					raw_results?.entities?.edge_groups?.filter(eg => [EDGE_GROUPING_CRITERIA.MAINLINE, EDGE_GROUPING_CRITERIA.USER_DEFINED_ROUTE].includes(eg.grouping_criteria))?.map(eg => ({
						id: eg.id,
						icon: IconRoute,
						title: 'Route',
						name: `${eg.road_name} (${eg.cardinal_direction})`,
						link: router.routes.roadway.useNavigation({
							params: {
								region_id: eg.region_id,
								subregion_id: eg.subregion_id,
								network_id: eg.network_id,
								roadway_id: eg.id,
							},
							query: {
								mode: current_mode,
								active_sidebar: 'right',
							},
							options: {
								preserve: true
							},
						}),
						similarity: eg.similarity,
					})) ?? []
				),

				...(
					raw_results?.entities?.edge_groups?.filter(eg => ![EDGE_GROUPING_CRITERIA.MAINLINE, EDGE_GROUPING_CRITERIA.USER_DEFINED_ROUTE].includes(eg.grouping_criteria))?.map(eg => ({
						id: eg.id,
						icon: IconRoad,
						title: 'Roadway',
						name: `${eg.road_name} (${eg.cardinal_direction})`,
						link: router.routes.roadway.useNavigation({
							query: {
								mode: current_mode,
							},
							params: {
								region_id: eg.region_id,
								subregion_id: eg.subregion_id,
								network_id: eg.network_id,
								roadway_id: eg.id,
							},
							options: {
								preserve: true
							},
						}),
						similarity: eg.similarity,
					})) ?? []
				),

			]

			const sorted_results = unsorted_results
				.toSorted((a, b) => {
					if (a.similarity > b.similarity) return -1
					else if (a.similarity < b.similarity) return 1

					if (a.name < b.name) return -1
					else if (a.name > b.name) return 1

					return 1
				})

			return sorted_results

		}

		const {
			customer_id,
			current_mode,
			search_token,
		} = vnode.state

		if (!search_token) {
			vnode.state.results = []
			return
		}

		vnode.state.loading = true
		m.redraw()

		const search_key = ''.concat(customer_id, current_mode, search_token)

		SearchModel.fetch(
			customer_id,
			search_token,
			{
				modes: [current_mode]
			},
		).then(raw_results => {
			// Check for stale fetch
			if (search_key !== vnode.state.search_key) return

			vnode.state.results = catalogResults(raw_results)

			vnode.state.loading = false

			m.redraw()
		})


	}, 200)

}

InputSearch.onbeforeupdate = vnode => {

	const {
		search_token,
	} = vnode.state

	const current_route = router.currentRoute()

	const current_mode = (current_route.name === 'roadway' && current_route.query?.mode) ? current_route.query.mode
		: current_route.name.startsWith('safety') ? MODES.SAFETY
		: current_route.name.startsWith('mobility') ? MODES.MOBILITY
		: MODES.SIGOPS

	const reset_key = ''.concat(vnode.attrs.customer_id, current_mode)
	const search_key = ''.concat(vnode.attrs.customer_id, current_mode, search_token)

	if (vnode.state.reset_key === reset_key && vnode.state.search_key === search_key) return

	if (vnode.state.reset_key !== reset_key) {
		vnode.state.reset_key = reset_key

		vnode.state.customer_id = vnode.attrs.customer_id
		vnode.state.current_mode = current_mode
		vnode.state.search_token = null
		vnode.state.results = []
	}

	if (vnode.state.search_key !== search_key) {
		vnode.state.search_key = search_key
	}

}

InputSearch.view = (vnode) => {

	const {
		loading,
		search_token,
		search,

		results,
	} = vnode.state

	if (!vnode.attrs.customer_id) return console.warn('InputSearch: Missing customer_id. Skipping render.')


	return m('div', { class: s.container }, [
		m('div', { class: s.head }, [
			m(Datalist, {
				has_options_as_buttons: true,

				ariaLabel: 'Search entities',
				class: s.search,
				datalist_class: s.datalist,
				option_class: s.option,
				loading,
				list: results?.map(x => {
					return {
						...x,
						component: m('div', {class: s.data_row}, [
							x.icon,
							x.title && m('span', {class: s.data_row_title}, `${x.title}:`),
							m('span', {class: s.data_row_name}, x.name),
						]),
					}
				}) || [],
				default_current_focus: -1,
				oninput: (text) => {
					vnode.state.results = []
					vnode.state.search_token = text

					search()
				},
				onselect: (item, setInputValue) => {
					item.link()
					setInputValue('')
				},
			}),
		]),
	])
}

export default InputSearch
